import {
  ButtonSecondaryDiv,
  RoundButton,
  RoundButtonDiv,
} from "@components/Buttons";
import { initializeApp } from "firebase/app";
import {
  GoogleAuthProvider,
  getAuth,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithPopup,
  OAuthProvider,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import axios from "axios";
import { validateEmail } from "../ContractEditing/utils/validEmail";
import React, { useEffect, useState, useContext } from "react";
import AppContext from "components/AppContext";
import { FcGoogle } from "react-icons/fc";
import { MdClose } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import {
  FIREBASE_CONFIG_SIGNATURE,
  SIGNATURE_DASH_URL,
} from "@constants/constants";
import SignatureLogin2FA from "components/SignatueLogin2FA";
import {
  handleVerify,
  get2FAInfo,
  updateLoginInfoOnlyBackend,
  updateLoginInfo,
} from "@utils/signature_owner";
import FB from "@utils/tracking/facebook";
import { posthog } from "posthog-js";

//# firebase -------------------------------------------------------------------
const app = initializeApp(FIREBASE_CONFIG_SIGNATURE);
const auth = getAuth(app);

//# component functions ----------------------------------------------------------------------------
function posthogIdentifyWrapper({ email }) {
  posthog?.identify(email, { email });
}

async function sendVerificationEmail({ cb }) {
  const { uid, refreshToken, email, accessToken } = auth.currentUser;
  const utmData = JSON.parse(localStorage.getItem("utmData") || "{}");
  let actionUrl = `${SIGNATURE_DASH_URL}/login?uid=${uid}&email=${email}&accessToken=${accessToken}`;
  for (let key in utmData) {
    actionUrl += `&${key}=${utmData[key]}`;
  }
  actionUrl += "&noTrackFB=1";
  const actionCodeSettings = {
    url: actionUrl,
  };
  let returnData = {};
  if (auth.currentUser !== null) {
    try {
      await sendEmailVerification(auth.currentUser, actionCodeSettings);
      console.log("Email Verification sent! Check your mailbox");
      returnData = {
        success: true,
        general: "Email Verification sent! Check your mailbox",
      };
      cb?.();
      posthogIdentifyWrapper({ email });
    } catch (error) {
      console.error("Error sending email verification:", error);
      returnData = {
        success: false,
        generalError: "Failed to send email verification. Please try again.",
      };
    }
  }
  return returnData;
}

const signInWithGoogle = async ({ setTwoFA }) => {
  const provider = new GoogleAuthProvider();
  const auth = getAuth();
  let returnData = {};

  try {
    const result = await signInWithPopup(auth, provider);
    await updateLoginInfoOnlyBackend(result, "google");

    const twoFaInfo = await get2FAInfo({ email: result?.user?.email || "" });

    if (twoFaInfo.enabled && twoFaInfo.verified) {
      console.log("2fa enabled and verified");
      setTwoFA((prev) => ({
        ...prev,
        data: { result, method: "google" },
        show: true,
      }));
      returnData = { success: true, twoFARequired: true };
      posthogIdentifyWrapper({ email: result?.user?.email || "" });
    } else {
      posthogIdentifyWrapper({ email: result?.user?.email || "" });
      const data = await updateLoginInfo(result, "google");
      returnData = data?.error
        ? { success: false, generalError: data.error }
        : { success: true, general: data.message };
    }
  } catch (error) {
    console.log("error", error);
    returnData = {
      success: false,
      generalError: "An error occurred. Please try again.",
    };
  }

  return returnData;
};

const signInWithMicrosoft = async ({ setTwoFA }) => {
  const provider = new OAuthProvider("microsoft.com");
  const auth = getAuth();
  let returnData = {};

  try {
    const result = await signInWithPopup(auth, provider);
    await updateLoginInfoOnlyBackend(result, "microsoft");

    const twoFaInfo = await get2FAInfo({ email: result?.user?.email || "" });

    if (twoFaInfo.enabled && twoFaInfo.verified) {
      console.log("2fa enabled and verified");
      setTwoFA((prev) => ({
        ...prev,
        data: { result, method: "microsoft" },
        show: true,
      }));
      returnData = { success: true, twoFARequired: true };
      posthogIdentifyWrapper({ email: result?.user?.email || "" });
    } else {
      posthogIdentifyWrapper({ email: result?.user?.email || "" });
      const data = await updateLoginInfo(result, "microsoft");
      returnData = data?.error
        ? { success: false, generalError: data.error }
        : { success: true, general: data.message };
    }
  } catch (error) {
    console.log("error", error);
    returnData = {
      success: false,
      generalError: "An error occurred. Please try again.",
    };
  }

  return returnData;
};

async function handleRegisterUser({ user, setUser, setEmailVerificationSent }) {
  let { email, password, confirmPassword } = user;
  console.log("handleRegisterUser", user);
  let returnData = {};

  try {
    if (!validateEmail(email)) {
      returnData = { email: "Please enter valid email" };
      return returnData;
    }
    if (password.trim() === "") {
      returnData = { password: "Please enter password" };
      return returnData;
    }
    if (confirmPassword.trim() === "") {
      returnData = { confirmPassword: "Please repeat the password" };
      return returnData;
    }
    if (password !== confirmPassword) {
      returnData = {
        confirmPassword: "Password and confirm password should be same",
      };
      return returnData;
    }
    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/;
    if (!passwordRegex.test(password)) {
      returnData = {
        password: "Weak Password",
      };
      return returnData;
    }

    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const registeredUser = userCredential.user;

    let data = await sendVerificationEmail({
      cb: () => {
        setEmailVerificationSent(true);
        setUser?.();
      },
    });

    returnData = data?.success
      ? {
          success: true,
          general:
            data?.general ||
            "Registration successful. Please verify your email.",
        }
      : {
          success: false,
          generalError: returnData?.generalError,
        };
  } catch (error) {
    console.log(error);
    returnData = {
      success: false,
      generalError: error?.message || "An error occurred during registration.",
    };
  }

  return returnData;
}

async function handleLogin({ user, setTwoFA }) {
  let { email, password } = user;
  console.log("handleLogin", user);

  if (!email) {
    return { email: "Email is required" };
  }

  if (!password) {
    return { password: "Password is required" };
  }

  let returnData = {};

  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const loggedInUser = userCredential.user;

    if (!loggedInUser?.emailVerified) {
      console.log("email not verified");
      return { email: "Please verify your email address, check your inbox." };
    }

    await updateLoginInfoOnlyBackend(userCredential, "email_password");
    const twoFaInfo = await get2FAInfo({ email });

    if (twoFaInfo.enabled && twoFaInfo.verified) {
      console.log("2fa enabled and verified");
      setTwoFA((prev) => ({
        ...prev,
        data: { result: userCredential, method: "email_password" },
        show: true,
      }));
      returnData = { success: true, twoFARequired: true };
    } else {
      const data = await updateLoginInfo(userCredential, "email_password");
      console.log({ data });
      returnData = data?.error
        ? { success: false, generalError: data.error }
        : { success: true, general: data.message };
    }
  } catch (error) {
    console.log("error", error);
    returnData = {
      success: false,
      generalError: "Email or Password is incorrect. Please try again.",
    };
  }
  return returnData;
}

//# individual components ----------------------------------------------------------------------------
const Message = ({ message, type = "error", center = false }) => {
  return (
    <div
      className={`${message ? "h-4" : "h-1"} ${
        type === "error" ? "text-red-500" : "text-green-600"
      } ${
        center ? "text-center" : ""
      } w-full text-xs mb-2 mt-0.5 transition-all duration-200`}
    >
      {message || ""}
    </div>
  );
};

const EmailRegister = ({
  user,
  setUser,
  setEmailVerificationSent,
  emailVerificationSent,
  setMessages,
  messages,
}) => {
  return !emailVerificationSent ? (
    <div className="w-full">
      <input
        autoFocus
        type="email"
        placeholder="Email"
        className="w-full p-2.5 rounded-md border-gray-700 border-2 focus:outline-none "
        value={user.email}
        onChange={(e) => setUser({ ...user, email: e.target.value })}
      />
      <Message message={messages?.email} />
      <input
        type="password"
        placeholder="Password"
        className="w-full p-2.5 rounded-md border-gray-700 border-2 focus:outline-none "
        value={user.password}
        onChange={(e) => setUser({ ...user, password: e.target.value })}
      />
      <Message message={messages?.password} />
      <input
        type="password"
        placeholder="Confirm Password"
        className="w-full p-2.5 rounded-md border-gray-700 border-2 focus:outline-none "
        value={user.confirmPassword}
        onChange={(e) => setUser({ ...user, confirmPassword: e.target.value })}
      />
      <Message message={messages?.confirmPassword} />
      <p className="text-xs text-gray-700 mb-2">
        Password must contain one captial letter, one small letter, one number
        and one special character and minimum 8 characters
      </p>
      <ButtonSecondaryDiv
        buttonText="Register"
        onclick={async () => {
          let messages = await handleRegisterUser({
            user,
            setUser: () => {
              setUser({ email: "", password: "", confirmPassword: "" });
            },
            setEmailVerificationSent,
          });
          console.log({ messages });
          if (messages?.success) {
            setMessages({ general: messages?.general });
          } else {
            setMessages(messages);
          }
        }}
      />
    </div>
  ) : (
    <div className="text-center w-full my-2">
      <h2 className="">Verification email sent</h2>
      <p>
        Verification email sent to your inbox, please verify your email to
        continue.
      </p>
    </div>
  );
};

const EmailLogin = ({
  user,
  setUser,
  setTwoFA,
  setMessages,
  messages,
  closePopup,
}) => {
  const { setGlobalData } = useContext(AppContext);
  return (
    <div className="w-full">
      <input
        autoFocus
        type="email"
        placeholder="Email"
        className="w-full p-2.5 rounded-md border-gray-700 border-2 focus:outline-none"
        value={user.email}
        onChange={(e) => setUser({ ...user, email: e.target.value })}
      />
      <Message message={messages?.email} />
      <input
        type="password"
        placeholder="Password"
        className="w-full p-2.5 rounded-md border-gray-700 border-2 focus:outline-none"
        value={user.password}
        onChange={(e) => setUser({ ...user, password: e.target.value })}
      />
      <Message message={messages?.password} />
      <p className="text-sm text-gray-500 mb-2 flex justify-end">
        <span
          onClick={() => {
            setGlobalData((prev) => ({
              ...prev,
              modals: {
                ...prev.modals,
                forgotPassword: true,
              },
            }));
          }}
          className="text-primary cursor-pointer"
        >
          Forgot Password?
        </span>
      </p>
      <ButtonSecondaryDiv
        buttonText="Login"
        onclick={async () => {
          let messages = await handleLogin({
            user,
            setTwoFA,
            setMessages,
          });
          if (messages?.success) {
            setMessages({ general: messages?.general });
          } else {
            setMessages(messages);
          }
        }}
      />
    </div>
  );
};

export const ForgotPassword = () => {
  const { setGlobalData } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");

  const closePopup = () => {
    setGlobalData((prev) => ({
      ...prev,
      modals: {
        ...prev.modals,
        forgotPassword: false,
      },
    }));
  };

  const handleForgotPassword = async () => {
    setLoading(true);
    if (!validateEmail(email)) return toast.error("Please enter valid email");
    const auth = getAuth();
    sendPasswordResetEmail(auth, email)
      .then(() => {
        // Password reset email sent!
        console.log("Password reset email sent!");
        toast.success("Password reset email sent!");
        // ph({ event: "OWNER:LOGIN:FORGOT_PASSWORD" });
        closePopup();
      })
      .catch((error) => {
        const errorMessage = error.message;
        console.log(errorMessage);
        toast.error(error.message);
        // ..
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div
      onClick={closePopup}
      className="fixed z-[1000] top-0 left-0 w-[100svw] h-[100svh] bg-black/95 flex justify-center items-center text-left"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="p-6 rounded-lg bg-white flex items-start flex-col w-[500px] w-max-[95%] m-2 relative"
      >
        <div className="w-full flex flex-col justify-center items-center">
          <div className="w-full flex flex-col justify-center items-center">
            <h2 className="text-2xl font-bold text-gray-700">
              Forgot Password
            </h2>
            <p className="text-gray-500 text-sm mt-2">
              Enter your email address and we will send you a link to reset your
              password.
            </p>
          </div>
          <div className="w-full flex flex-col justify-center items-center mt-6">
            <input
              type="email"
              placeholder="Email"
              className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button
              disabled={loading}
              onClick={handleForgotPassword}
              className="w-full bg-primary text-white disabled:bg-primary/80 rounded-md px-4 py-2 mt-4 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent flex justify-center items-center gap-4"
            >
              Send Reset Link
              {loading && (
                <span className="w-2 h-2 border-t rounded-full animate-spin"></span>
              )}
            </button>
          </div>
          <div className="w-full flex justify-center items-center mt-4">
            <span
              onClick={closePopup}
              className="text-primary hover:underline cursor-pointer text-sm"
            >
              Back To Login
            </span>
          </div>
        </div>
        <MdClose
          onClick={closePopup}
          className="absolute top-4 right-4 text-gray-500 cursor-pointer text-2xl"
        />
      </div>
    </div>
  );
};

//# main component ----------------------------------------------------------------------------
const SignatureRegister = ({ isPopupOpen, setIsPopupOpen, mode, setMode }) => {
  const modeData = {
    register: {
      title: "Register",
      buttonText: "Register",
    },
    login: {
      title: "Login",
      buttonText: "Login",
    },
  };
  const [user, setUser] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [twoFA, setTwoFA] = useState({
    show: false,
    data: null,
  });
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [messages, setMessages] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    general: "",
  });

  if (!isPopupOpen) return null;

  function closePopup() {
    setIsPopupOpen(false);
    setUser({
      email: "",
      password: "",
      confirmPassword: "",
    });
    setEmailVerificationSent(false);
    setTwoFA({ show: false, data: null });
    setMessages({
      email: "",
      password: "",
      confirmPassword: "",
      general: "",
    });
  }

  return (
    <div
      onClick={closePopup}
      className="fixed z-[1000] top-0 left-0 w-[100svw] h-[100svh] bg-black/20 flex justify-center items-center text-left"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="p-6 rounded-lg bg-white flex items-start flex-col w-[500px] w-max-[95%] m-2 relative"
      >
        <SignatureLogin2FA
          userData={twoFA.data}
          show={twoFA.show}
          setShow={(value) => setTwoFA((prev) => ({ ...prev, show: value }))}
        />
        <MdClose
          className="absolute top-6 right-6 cursor-pointer text-3xl"
          onClick={closePopup}
        />
        <h2 className="mb-6 text-2xl text-center w-full">
          {modeData?.[mode]?.title} Now
        </h2>
        {mode === "register" ? (
          <EmailRegister
            user={user}
            setUser={setUser}
            setEmailVerificationSent={setEmailVerificationSent}
            emailVerificationSent={emailVerificationSent}
            setMessages={setMessages}
            messages={messages}
            closePopup={closePopup}
          />
        ) : (
          <EmailLogin
            user={user}
            setUser={setUser}
            setTwoFA={setTwoFA}
            setMessages={setMessages}
            messages={messages}
            closePopup={closePopup}
          />
        )}
        <div className="mb-1"></div>
        <Message
          message={messages?.generalError || messages?.general}
          type={messages?.generalError ? "error" : "success"}
          center={true}
        />
        <div className="">
          {mode === "register" ? (
            <p className="text-sm font-light">
              Already have an account?{" "}
              <span
                onClick={() => setMode("login")}
                className="font-medium text-violet-500 cursor-pointer"
              >
                Login
              </span>
            </p>
          ) : (
            <p className="text-sm font-light">
              Don&apos;t have an account?{" "}
              <span
                onClick={() => setMode("register")}
                className="font-medium text-violet-500 cursor-pointer"
              >
                Register
              </span>
            </p>
          )}
        </div>
        <div className="w-full flex items-center justify-center gap-2 my-6 mt-2.5">
          <div className="border-b w-[100%] border-gray-700"></div>
          <p className="text-gray-700 text-sm">OR</p>
          <div className="border-b w-[100%] border-gray-700"></div>
        </div>
        <div className="w-full flex flex-col gap-2">
          <ButtonSecondaryDiv
            textClassName={"min-w-[200px] ml-2"}
            buttonText={`${modeData?.[mode]?.buttonText} with Google`}
            icon={<FcGoogle />}
            onclick={async () => {
              let messages = await signInWithGoogle({ setTwoFA });
              if (messages?.success) {
                setMessages({ general: messages?.general });
              } else {
                setMessages(messages);
              }
            }}
          />
          <ButtonSecondaryDiv
            textClassName={"min-w-[200px] ml-2"}
            buttonText={`${modeData?.[mode]?.buttonText} with Microsoft`}
            icon={
              <span className="py-[3px] pointer-events-none" tabIndex="-1">
                <object
                  type="image/svg+xml"
                  data="https://s3-eu-west-1.amazonaws.com/cdn-testing.web.bas.ac.uk/scratch/bas-style-kit/ms-pictogram/ms-pictogram.svg"
                  className="x-icon w-6 h-6"
                  aria-label="Microsoft"
                  tabIndex="-1"
                ></object>
              </span>
            }
            onclick={async () => {
              let messages = await signInWithMicrosoft({ setTwoFA });
              if (messages?.success) {
                setMessages({ general: messages?.general });
              } else {
                setMessages(messages);
              }
            }}
          />
        </div>
      </div>
      <ToastContainer
        className={`text-xs`}
        // theme="dark"
        position="bottom-right"
      />
    </div>
  );
};

export default SignatureRegister;
